import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { statsService } from '../services/statsService';
import { useNavigate } from 'react-router-dom';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import './Profile.css';

const Profile = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('general'); // 'general', 'local', 'multiplayer'

  useEffect(() => {
    const loadStats = async () => {
      if (currentUser) {
        const data = await statsService.getUserStats(currentUser.uid);
        setStats(data);
      }
      setLoading(false);
    };
    loadStats();
  }, [currentUser]);

  if (loading) {
    return (
      <div className="profile-container">
        <nav className="profile-nav">
          <button className="btn-back" onClick={() => navigate('/')} title="Torna alla Home">
            ←
          </button>
          <h1>Statistiche Giocatore</h1>
        </nav>
        <LoadingSpinner fullPage />
      </div>
    );
  }

  const averageScore = stats?.totalGames > 0 ? (stats.totalScore / stats.totalGames).toFixed(1) : 0;

  // Format history for graph (taking last 20 games)
  const chartData =
    stats?.scoreHistory?.slice(-20).map((h, i) => ({
      name: 'G' + (i + 1),
      punti: h.score,
    })) || [];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="tooltip-label">{`Punteggio`}</p>
          <p className="tooltip-value">{`${payload[0].value} pts`}</p>
        </div>
      );
    }
    return null;
  };

  // Filter history based on active tab
  const filteredHistory = stats?.scoreHistory
    ? [...stats.scoreHistory]
        .filter((game) => {
          if (activeTab === 'local') return game.mode === 'local' || !game.mode;
          if (activeTab === 'multiplayer') return game.mode === 'multiplayer';
          return true;
        })
        .reverse()
    : [];

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('it-IT');
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="profile-container">
      {/* ... navigation ... */}
      <nav className="profile-nav">
        <button className="btn-back" onClick={() => navigate('/')} title="Torna alla Home">
          ←
        </button>
        <h1>Statistiche Giocatore</h1>
      </nav>

      <div className="profile-content">
        {/* Column 1: Profile Info & Navigation */}
        <aside className="profile-sidebar">
          <div className="profile-info-card">
            <div className="profile-user-header">
              <div className="avatar-wrapper">
                <div className="avatar">
                  {currentUser?.photoURL ? (
                    <img src={currentUser.photoURL} alt="User" />
                  ) : (
                    currentUser?.displayName?.charAt(0) || 'U'
                  )}
                </div>
              </div>
              <h2 className="username">{currentUser?.displayName || 'Giocatore'}</h2>
            </div>

            <div className="profile-footer-actions">
              <button
                className={`btn-tab ${activeTab === 'general' ? 'active' : ''}`}
                onClick={() => setActiveTab('general')}
              >
                📊 Generali
              </button>
              <button
                className={`btn-tab ${activeTab === 'local' ? 'active' : ''}`}
                onClick={() => setActiveTab('local')}
              >
                🎮 Partite in Locale
              </button>
              <button
                className={`btn-tab ${activeTab === 'multiplayer' ? 'active' : ''}`}
                onClick={() => setActiveTab('multiplayer')}
              >
                🌐 Multiplayer
              </button>
            </div>
          </div>
        </aside>

        {/* Conditional Main Content */}
        {activeTab === 'general' ? (
          <>
            {/* ... general dashboard ... */}
            <div className="profile-stats-middle">
              <div className="hero-stat-card">
                <div className="hero-stat-icon">🎮</div>
                <div className="hero-stat-info">
                  <span className="hero-stat-label">Partite</span>
                  <span className="hero-stat-value">{stats?.totalGames || 0}</span>
                </div>
              </div>
              <div className="hero-stat-card">
                <div className="hero-stat-icon">🔥</div>
                <div className="hero-stat-info">
                  <span className="hero-stat-label">Record</span>
                  <span className="hero-stat-value">{stats?.maxScore || 0}</span>
                </div>
              </div>

              <div className="quick-stats-grid">
                <div className="mini-stat">
                  <span className="mini-stat-label">Parole</span>
                  <span className="mini-stat-value">{stats?.totalWords || 0}</span>
                </div>
                <div className="mini-stat">
                  <span className="mini-stat-label">Passi</span>
                  <span className="mini-stat-value">{stats?.totalPasses || 0}</span>
                </div>
                <div className="mini-stat">
                  <span className="mini-stat-label">Media</span>
                  <span className="mini-stat-value">{averageScore}</span>
                </div>
                <div className="mini-stat">
                  <span className="mini-stat-label">Velocità</span>
                  <span className="mini-stat-value">
                    {stats?.fastestWordMs ? (stats.fastestWordMs / 1000).toFixed(1) + 's' : '-'}
                  </span>
                </div>
              </div>
            </div>

            <div className="chart-card">
              <h3>
                <span style={{ opacity: 0.7 }}>📈</span> Andamento Ultime 20 Partite
              </h3>
              <div className="chart-wrapper">
                {chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={chartData}
                      margin={{ top: 10, right: 10, left: -20, bottom: 20 }}
                    >
                      <defs>
                        <linearGradient id="colorPunti" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="var(--color-primary)" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="var(--color-primary)" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="lineGradient" x1="0" y1="0" x2="1" y2="0">
                          <stop offset="0%" stopColor="var(--color-primary)" />
                          <stop offset="100%" stopColor="var(--color-secondary)" />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke="var(--color-border)"
                      />
                      <XAxis dataKey="name" hide={true} />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 10, fill: 'var(--color-text-muted)' }}
                        domain={[0, (dataMax) => Math.max(dataMax + 2, 10)]}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Area
                        type="monotone"
                        dataKey="punti"
                        stroke="url(#lineGradient)"
                        strokeWidth={4}
                        fillOpacity={1}
                        fill="url(#colorPunti)"
                        animationDuration={1500}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="no-data-wrapper">
                    <p className="no-data" style={{ fontSize: '0.8rem' }}>
                      Gioca la tua prima partita!
                    </p>
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          /* History List View */
          <div className="chart-card" style={{ flex: 1 }}>
            <div className="history-container">
              <div className="history-header">
                <span className="header-label">Data</span>
                <span className="header-label center" title="Corrette">
                  ✅
                </span>
                <span className="header-label center" title="Sbagliate">
                  ❌
                </span>
                <span className="header-label center" title="Passi">
                  ⚪
                </span>
                <span className="header-label" style={{ textAlign: 'right' }}>
                  Punteggio
                </span>
              </div>

              <div className="history-list">
                {filteredHistory.length > 0 ? (
                  filteredHistory.map((game, idx) => (
                    <div key={game.date ?? idx} className="history-item slim">
                      <div className="history-date-it">{formatDate(game.date)}</div>
                      <div className="history-stat-value success">{game.wordsGuessed}</div>
                      <div className="history-stat-value error">{game.wrongAnswers || 0}</div>
                      <div className="history-stat-value white">{game.passesUsed || 0}</div>
                      <div className="history-stat-value score-yellow">{game.score}</div>
                    </div>
                  ))
                ) : (
                  <div className="no-history">Non ci sono ancora partite registrate.</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
